import { useTranslation } from "@opendash/i18n";
import { Icon } from "@opendash/icons";
import { stringToTag } from "@opendash/ui";
import { Avatar, Divider, List, Tooltip, Typography } from "antd";
import dayjs from "dayjs";
import { observer } from "mobx-react-lite";
import React, { useMemo } from "react";
import { useTicketData } from "../..";
import { ArticleCollapsable, MessageData } from "../../../messages";

type Props = {
  showdivider?: boolean;
  hideForm?: boolean;
  hideTime?: boolean;
  reload?: () => void;
};
/**
 * @description Displays a page with detailed information about a single ticket.
 * @param id Needs the objectId of the ticket in navigation (/ticket/{objectId})
 */
export default observer((props: Props) => {
  const t = useTranslation();
  const ticketData = useTicketData();

  const filteredMessages = useMemo(() => {
    return ticketData.messages
      ?.filter((msg) => {
        const bodyData = msg?.data as MessageData;
        return bodyData?.type === "userinput";
      })
      .sort((a, b) =>
        dayjs(a.get("displayAt")).isAfter(dayjs(b.get("displayAt"))) ? -1 : 1
      );
  }, [ticketData.messages]);

  return (
    <>
      {props.showdivider && (
        <Divider orientation="left" style={{ marginTop: 150 }}>
          {t("maintenance:ticket.recentmessage.headline")}
        </Divider>
      )}
      <List
        itemLayout="horizontal"
        dataSource={
          filteredMessages?.map((msg) => {
            const translation = msg?.data?.translation;
            return {
              title: stringToTag(t(msg?.title, translation), "'"),
              description: msg?.content,
              createdAt: msg?.createdAt,
              avatar: (
                <Avatar
                  style={{
                    background: "--var-color-primary",
                    verticalAlign: "middle",
                  }}
                  icon={
                    <Icon
                      icon={
                        msg?.data?.type === "userinput"
                          ? "fa:user"
                          : msg?.data?.type === "media"
                            ? "fa:image"
                            : "fa:right"
                      }
                    />
                  }
                />
              ),
            };
          }) || []
        }
        renderItem={(item) => {
          return (
            <List.Item
              actions={[
                <>
                  {props.hideTime && (
                    <Tooltip
                      title={dayjs(item.createdAt).format("DD.MM.YYYY HH:mm")}
                    >
                      <Icon icon="fa:clock" />
                    </Tooltip>
                  )}
                  {!props.hideTime && (
                    <Typography.Text type="secondary">
                      {dayjs(item.createdAt).format("DD.MM.YYYY HH:mm")}
                    </Typography.Text>
                  )}
                </>,
              ]}
            >
              <List.Item.Meta
                title={item.title}
                description={item.description}
                avatar={item.avatar}
              />
            </List.Item>
          );
        }}
      />

      {ticketData && !props.hideForm && (
        <ArticleCollapsable
          showdivider={true}
          referencedObject={ticketData.ticket}
          shownewmessage={true}
          showAsTodo
          onOkUpdate={() => {
            props.reload?.();
          }}
        />
      )}
    </>
  );
});
